import AddIcon from '@mui/icons-material/Add';
import CachedIcon from '@mui/icons-material/Cached';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CloseIcon from '@mui/icons-material/Close';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import RemoveIcon from '@mui/icons-material/Remove';
import ViewWeekOutlinedIcon from '@mui/icons-material/ViewWeekOutlined';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  IconButton,
  Slider,
  Tab,
  Tabs,
  Theme,
  Typography,
} from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import React, { FC, useEffect, useState } from 'react';
import Colors from '../../utils/color';
import {
  DEFAULT_DISPLAY_PREFERENCE,
  DisplayPreference,
} from '../../utils/displayPreference';
import DisplayPreferenceTab from './DisplayPreferenceTab';

/** Default pane split (Left / Middle / Right), as percentages summing to 100. */
export const DEFAULT_PANE_SIZES: [number, number, number] = [25, 50, 25];
/**
 * Per-pane width clamp — mirrors the PanelGroup min/maxSize so typed + dragged
 * agree. Every pane keeps at least 15%; with three panes that lets any one pane
 * grow to 70% (100 − 15 − 15) by borrowing from the other two.
 */
export const MIN_PANE_SIZE = 15;
export const MAX_PANE_SIZE = 70;

/**
 * Per-card height clamp for the vertical stacks inside the left & right panes.
 * Each stack is a two-card split summing to 100, each card at least 15% — so the
 * reachable max for one card is 85% (100 − 15).
 */
export const CARD_MIN_HEIGHT = 15;
export const CARD_MAX_HEIGHT = 85;
/** Default left-pane stack split [Transcript, AI Copilot]. */
export const LEFT_STACK_DEFAULT_SIZES: [number, number] = [75, 25];
/** Default right-pane stack split [Agent Cart, POS Cart]. */
export const RIGHT_STACK_DEFAULT_SIZES: [number, number] = [75, 25];

/**
 * HITL-1648 — the My Cart | Live order column split, inside the right pane's
 * top card.
 *
 * A WIDTH split, not a height: it divides one card into two columns. The clamp
 * mirrors `RightSidebar`'s `minSize={20}` on both panels so a typed value and a
 * dragged one agree — with two columns, 20% each side lets either grow to 80%.
 *
 * Even by default, because neither column is the senior one: the agent watches
 * in one and builds in the other.
 */
export const CART_SPLIT_MIN = 20;
export const CART_SPLIT_MAX = 80;
export const CART_SPLIT_DEFAULT_SIZES: [number, number] = [50, 50];

/** The full set of layout values the dialog commits on Apply. */
export interface LayoutPreferenceValues {
  panes: [number, number, number];
  leftStack: [number, number];
  rightStack: [number, number];
  cartSplit: [number, number];
}

interface SplitItem {
  label: string;
  testid: string;
}

// All sliders share one accent — the app primary — so the dialog reads as
// neutral controls rather than a rainbow.
const PANE_ITEMS: SplitItem[] = [
  { label: 'Left', testid: 'pane-size-left' },
  { label: 'Middle', testid: 'pane-size-middle' },
  { label: 'Right', testid: 'pane-size-right' },
];
const LEFT_STACK_ITEMS: SplitItem[] = [
  { label: 'Transcript', testid: 'card-height-transcript' },
  { label: 'AI Copilot', testid: 'card-height-copilot' },
];
const RIGHT_STACK_ITEMS: SplitItem[] = [
  { label: 'Agent Cart', testid: 'card-height-agent-cart' },
  { label: 'POS Cart', testid: 'card-height-pos-cart' },
];
// HITL-1648. Named for what the agent sees on the two column headers.
const CART_SPLIT_ITEMS: SplitItem[] = [
  { label: 'My Cart', testid: 'cart-width-my-cart' },
  { label: 'Live Order', testid: 'cart-width-live-order' },
];

interface Preset {
  label: string;
  sizes: number[];
}

// "Wide X" = that pane at 50%, the other two at 25% each.
const PANE_PRESETS: Preset[] = [
  { label: 'Balanced', sizes: [33, 34, 33] },
  { label: 'Wide transcript', sizes: [50, 25, 25] },
  { label: 'Wide menu', sizes: [25, 50, 25] },
  { label: 'Wide cart', sizes: [25, 25, 50] },
];
const LEFT_STACK_PRESETS: Preset[] = [
  { label: 'Balanced', sizes: [50, 50] },
  { label: 'Tall transcript', sizes: [75, 25] },
  { label: 'Tall copilot', sizes: [25, 75] },
];
const RIGHT_STACK_PRESETS: Preset[] = [
  { label: 'Balanced', sizes: [50, 50] },
  { label: 'Tall agent cart', sizes: [75, 25] },
  { label: 'Tall POS cart', sizes: [25, 75] },
];
// HITL-1648: "wide Live order" is the one the story was asked for — an agent
// reading an order back wants the column the customer is getting.
const CART_SPLIT_PRESETS: Preset[] = [
  { label: 'Even', sizes: [50, 50] },
  { label: 'Wide Live Order', sizes: [25, 75] },
  { label: 'Wide My Cart', sizes: [75, 25] },
];

// Single source of truth for the section headings ("QUICK PRESETS", "LEFT
// PANE", etc.) so they render identically — same as the LIVE TRANSCRIPT
// heading. Applied via sx to keep the app font ('Prompt') and avoid the
// makeStyles-vs-emotion ordering trap (see notes in the JSX).
const sectionHeadingSx = (theme: Theme) => ({
  color: Colors.readBack,
  fontFamily: theme.typography.fontFamily,
  fontSize: '0.8rem',
  fontWeight: 700,
  letterSpacing: '0.04em',
  textTransform: 'uppercase' as const,
});

// Shared style for the matching status labels — the "Total: N%" banner label and
// the "Reset to default" button — so they render identically (size, weight,
// family, spacing, no caps). Colour is applied per-usage (green ok / red error).
const statusLabelSx = (theme: Theme) => ({
  fontFamily: theme.typography.fontFamily,
  fontSize: '0.75rem',
  fontWeight: 700,
  letterSpacing: 'normal',
  textTransform: 'none' as const,
});

interface LayoutPreferenceDialogProps {
  open: boolean;
  onClose: () => void;
  /** Live pane widths (percentages) — reflects manual dragging when opened. */
  paneSizes: number[];
  /** Live left-pane stack heights [Transcript, AI Copilot]. */
  leftStackSizes: number[];
  /** Live right-pane stack heights [Agent Cart, POS Cart]. */
  rightStackSizes: number[];
  /** HITL-1648: live column widths [My Cart, Live Order]. */
  cartSplitSizes: number[];
  /** Whether the POS Cart card is present (pos_cart flag). */
  isPosCartEnabled: boolean;
  /**
   * HITL-1648: whether the two-column cart split exists at all
   * (`hitl_shadow_mode`). With it off there is one column and no divider, so
   * the row is hidden rather than shown inert.
   */
  isShadowModeEnabled: boolean;
  onApply: (values: LayoutPreferenceValues) => void;
  onReset: () => void;
  /**
   * HITL-1695: 7.0 adds a third tab, Display, and the dialog becomes
   * "Preferences". With it off the dialog is the two-tab Layout preference
   * dialog, unchanged.
   */
  isHitl7?: boolean;
  /** HITL-1695: the saved display choice, staged into the tab on open. */
  displayPreference?: DisplayPreference;
  /** HITL-1695: commits the Display tab on Apply (7.0 only). */
  onApplyDisplay?: (preference: DisplayPreference) => void;
}

const useStyles = makeStyles((theme: Theme) => ({
  titleBar: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: theme.spacing(1.5),
    padding: theme.spacing(2, 3, 1),
  },
  titleLeft: {
    display: 'flex',
    alignItems: 'center',
    // Same tight icon↔label gap as the LIVE TRANSCRIPT header.
    gap: theme.spacing(0.75),
  },
  titleIcon: {
    // Small inline icon in the transcript-blue, mirroring the LIVE TRANSCRIPT
    // header (no filled circle).
    color: Colors.readBack,
    fontSize: 18,
    display: 'flex',
    alignItems: 'center',
    flexShrink: 0,
  },
  // Type scale — body is 0.75rem, the app's standard text size:
  //   section headings  0.8rem  / 700 / uppercase / blue  -> sectionHeadingSx
  //   pane labels       0.75rem / 600
  //   body              0.75rem / 400   (descriptions, hints, %, preset labels)
  //   pane value        1rem    / 700   (the one prominent stepper number)
  desc: {
    color: theme.palette.text.secondary,
    lineHeight: 1.5,
  },
  paneRow: {
    display: 'flex',
    gap: theme.spacing(1.5),
    // Section gap — same value used before "Quick presets".
    marginTop: theme.spacing(2),
  },
  paneCard: {
    flex: 1,
    minWidth: 0,
    border: `1px solid ${Colors.alto}`,
    borderRadius: theme.spacing(1.5),
    // Compact — the cards were reading too large at spacing(2).
    padding: theme.spacing(1.25, 1),
    textAlign: 'center',
  },
  stepperRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: theme.spacing(0.5),
  },
  stepBtn: {
    border: `1px solid ${Colors.alto}`,
    borderRadius: theme.spacing(1),
  },
  paneValue: {
    // The one prominent number in the dialog — kept a touch larger than body.
    fontSize: '1rem',
    fontWeight: 700,
    minWidth: 30,
  },
  pct: {
    fontSize: '0.75rem',
    color: theme.palette.text.secondary,
    marginRight: theme.spacing(0.5),
  },
  // Total banner — one block in the stack; same 24px gap above as every other.
  totalBar: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1.5),
    marginTop: theme.spacing(2),
    padding: theme.spacing(1.25, 2),
    borderRadius: theme.spacing(1),
    backgroundColor: '#F4F6F8',
  },
  totalIconOk: {
    color: Colors.welcome,
  },
  totalIconError: {
    color: theme.palette.error.main,
  },
  totalHint: {
    color: theme.palette.text.secondary,
    fontSize: '0.75rem',
  },
  presetRow: {
    display: 'flex',
    // All presets share one row, each an equal-width column card.
    flexWrap: 'nowrap',
    gap: theme.spacing(1),
  },
  presetCard: {
    flex: 1,
    minWidth: 0,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: theme.spacing(0.75),
    padding: theme.spacing(1.25, 1),
    border: `1px solid ${Colors.alto}`,
    borderRadius: theme.spacing(1.5),
    backgroundColor: Colors.white,
    cursor: 'pointer',
    font: 'inherit',
    color: 'inherit',
    '&:hover': {
      backgroundColor: Colors.porcelain,
    },
  },
  presetActive: {
    borderColor: theme.palette.primary.main,
    boxShadow: `inset 0 0 0 1px ${theme.palette.primary.main}`,
  },
  presetLabel: {
    fontSize: '0.75rem',
    // Regular weight — the preset cards shouldn't shout.
    fontWeight: theme.typography.fontWeightRegular,
    textAlign: 'center',
    lineHeight: 1.25,
    // Wrap (never clip) so long labels stay readable in one row.
    maxWidth: '100%',
  },
  glyph: {
    display: 'flex',
    gap: 2,
    width: 30,
    height: 20,
    flexShrink: 0,
  },
  glyphBar: {
    backgroundColor: Colors.daintree,
    borderRadius: 2,
  },
  posOffNote: {
    marginTop: theme.spacing(2),
    padding: theme.spacing(1.25, 2),
    borderRadius: theme.spacing(1),
    backgroundColor: '#F4F6F8',
    color: theme.palette.text.secondary,
    fontSize: '0.75rem',
    lineHeight: 1.5,
  },
}));

/**
 * A group of per-item steppers + sliders that must sum to 100 — the shared
 * control behind BOTH the pane-width group (3 items) and each card-height stack
 * (2 items), so they look and behave identically.
 */
const SplitGroup: FC<{
  items: SplitItem[];
  values: number[];
  min: number;
  max: number;
  unitLabel: string;
  onChange: (index: number, value: number) => void;
}> = ({ items, values, min, max, unitLabel, onChange }) => {
  const classes = useStyles();
  const clamp = (value: number) => Math.min(max, Math.max(min, value));
  return (
    <div className={classes.paneRow}>
      {items.map((item, index) => (
        <div key={item.label} className={classes.paneCard}>
          <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, mb: 1 }}>
            {item.label}
          </Typography>
          <div className={classes.stepperRow}>
            <IconButton
              className={classes.stepBtn}
              size="small"
              onClick={() => onChange(index, clamp(values[index] - 1))}
              disabled={values[index] <= min}
              aria-label={`Decrease ${item.label}`}
            >
              <RemoveIcon fontSize="small" />
            </IconButton>
            <span className={classes.paneValue} data-testid={item.testid}>
              {values[index]}
            </span>
            <span className={classes.pct}>%</span>
            <IconButton
              className={classes.stepBtn}
              size="small"
              onClick={() => onChange(index, clamp(values[index] + 1))}
              disabled={values[index] >= max}
              aria-label={`Increase ${item.label}`}
            >
              <AddIcon fontSize="small" />
            </IconButton>
          </div>
          <Slider
            size="small"
            value={values[index]}
            min={min}
            max={max}
            step={1}
            onChange={(_event, value) => onChange(index, value as number)}
            // py tightens the slider's default touch-padding so the card
            // doesn't have airy empty space below the track.
            sx={(theme) => ({
              color: theme.palette.primary.main,
              mt: 0.5,
              py: 0.75,
            })}
            aria-label={`${item.label} ${unitLabel}`}
          />
        </div>
      ))}
    </div>
  );
};

/** The green/red "Total: N%" validity banner shared by every split group. */
const TotalBanner: FC<{
  total: number;
  isValid: boolean;
  validHint: string;
  invalidHint: string;
}> = ({ total, isValid, validHint, invalidHint }) => {
  const classes = useStyles();
  return (
    <div className={classes.totalBar}>
      {isValid ? (
        <CheckCircleIcon className={classes.totalIconOk} />
      ) : (
        <ErrorOutlineIcon className={classes.totalIconError} />
      )}
      <Typography
        component="span"
        sx={(theme) => ({
          ...statusLabelSx(theme),
          color: isValid ? Colors.welcome : theme.palette.error.main,
        })}
      >
        Total: {total}%
      </Typography>
      <span className={classes.totalHint}>
        {isValid ? validHint : invalidHint}
      </span>
    </div>
  );
};

/**
 * The quick-preset cards shared by every split group. `orientation` controls
 * the mini glyph: horizontal bars for pane widths, stacked bars for card
 * heights.
 */
const PresetRow: FC<{
  presets: Preset[];
  values: number[];
  orientation: 'horizontal' | 'vertical';
  onSelect: (sizes: number[]) => void;
}> = ({ presets, values, orientation, onSelect }) => {
  const classes = useStyles();
  const active = presets.find((preset) =>
    preset.sizes.every((size, index) => size === values[index])
  );
  return (
    <div className={classes.presetRow}>
      {presets.map((preset) => {
        const isActive = preset.label === active?.label;
        const widest = Math.max(...preset.sizes);
        const hasWide = preset.sizes.some((size) => size >= 45);
        return (
          <button
            key={preset.label}
            type="button"
            onClick={() => onSelect(preset.sizes.slice())}
            className={`${classes.presetCard} ${
              isActive ? classes.presetActive : ''
            }`}
          >
            <span
              className={classes.glyph}
              style={{
                flexDirection: orientation === 'vertical' ? 'column' : 'row',
              }}
            >
              {preset.sizes.map((size, index) => (
                <span
                  key={index}
                  className={classes.glyphBar}
                  style={{
                    flexGrow: size,
                    opacity: hasWide ? (size === widest ? 1 : 0.3) : 0.9,
                  }}
                />
              ))}
            </span>
            <span className={classes.presetLabel}>{preset.label}</span>
          </button>
        );
      })}
    </div>
  );
};

const sum = (values: number[]) => values.reduce((acc, v) => acc + (v || 0), 0);

// Snapshot a live stack split for display; if it's missing / collapsed /
// out-of-range (e.g. a transient collapsed size like [92, 8]), fall back to the
// stack default so the dialog always opens on clean, valid numbers.
const sanitizeStack = (
  sizes: number[],
  fallback: [number, number],
  // The clamp belongs to the SPLIT, not to this helper. It defaulted to the card
  // heights while they were the only caller; the cart columns clamp at 20/80, so
  // passing 15/85 for them would sanitize a value the dialog then refuses —
  // opening on a number that blocks Apply the moment it appears. Unreachable
  // today, because nothing can store an out-of-clamp split, but only by accident.
  min: number = CARD_MIN_HEIGHT,
  max: number = CARD_MAX_HEIGHT
): number[] => {
  if (!Array.isArray(sizes) || sizes.length !== 2) return fallback.slice();
  const rounded = sizes.map((size) => Math.round(size));
  const inRange = rounded.every((value) => value >= min && value <= max);
  return sum(rounded) === 100 && inRange ? rounded : fallback.slice();
};

/**
 * The "Layout preference" dialog. Two tabs — pane Width (the 3-pane horizontal
 * split) and card Height (the vertical card stacks inside the left & right
 * panes) — using the SAME stepper + slider + total-banner + preset controls.
 * Editing values resizes the live panes/cards on Apply (via `onApply` ->
 * setLayout); opening the dialog reflects the latest live sizes so a manual drag
 * shows up here too.
 */
const LayoutPreferenceDialog: FC<LayoutPreferenceDialogProps> = ({
  open,
  onClose,
  paneSizes,
  leftStackSizes,
  rightStackSizes,
  cartSplitSizes,
  isPosCartEnabled,
  isShadowModeEnabled,
  onApply,
  onReset,
  isHitl7 = false,
  displayPreference = DEFAULT_DISPLAY_PREFERENCE,
  onApplyDisplay,
}) => {
  const classes = useStyles();
  const [tab, setTab] = useState(0);
  const [paneValues, setPaneValues] = useState<number[]>(() =>
    DEFAULT_PANE_SIZES.slice()
  );
  const [leftValues, setLeftValues] = useState<number[]>(() =>
    LEFT_STACK_DEFAULT_SIZES.slice()
  );
  const [rightValues, setRightValues] = useState<number[]>(() =>
    RIGHT_STACK_DEFAULT_SIZES.slice()
  );
  const [cartValues, setCartValues] = useState<number[]>(() =>
    CART_SPLIT_DEFAULT_SIZES.slice()
  );
  // "Reset all to default" stages the defaults into the working values but does
  // NOT commit — the user still clicks Apply, consistent with every other
  // control. This flag remembers that the pending Apply is a reset, so it clears
  // the saved keys (restoring true out-of-box behaviour, e.g. the arrival
  // auto-expand) rather than pinning an explicit default. Any manual edit after
  // a reset clears the flag, so Apply then saves the edited values as usual.
  const [pendingReset, setPendingReset] = useState(false);
  // HITL-1695: staged like every other value — nothing is saved until Apply.
  const [displayValues, setDisplayValues] = useState<DisplayPreference>(
    DEFAULT_DISPLAY_PREFERENCE
  );

  // Reflect the latest live sizes (including manual drags) each time it opens,
  // and always land on the Width tab.
  useEffect(() => {
    if (open) {
      setTab(0);
      setPaneValues(paneSizes.map((size) => Math.round(size)));
      setLeftValues(sanitizeStack(leftStackSizes, LEFT_STACK_DEFAULT_SIZES));
      setRightValues(sanitizeStack(rightStackSizes, RIGHT_STACK_DEFAULT_SIZES));
      setCartValues(
        sanitizeStack(
          cartSplitSizes,
          CART_SPLIT_DEFAULT_SIZES,
          CART_SPLIT_MIN,
          CART_SPLIT_MAX
        )
      );
      setDisplayValues(displayPreference);
      setPendingReset(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const paneTotal = sum(paneValues);
  const panesValid =
    paneTotal === 100 &&
    paneValues.every(
      (value) => value >= MIN_PANE_SIZE && value <= MAX_PANE_SIZE
    );
  const leftTotal = sum(leftValues);
  const leftValid =
    leftTotal === 100 &&
    leftValues.every(
      (value) => value >= CARD_MIN_HEIGHT && value <= CARD_MAX_HEIGHT
    );
  const rightTotal = sum(rightValues);
  const rightValid =
    rightTotal === 100 &&
    rightValues.every(
      (value) => value >= CARD_MIN_HEIGHT && value <= CARD_MAX_HEIGHT
    );
  const cartTotal = sum(cartValues);
  const cartValid =
    cartTotal === 100 &&
    cartValues.every(
      (value) => value >= CART_SPLIT_MIN && value <= CART_SPLIT_MAX
    );
  // Each nested split only needs to validate when its divider exists: the right
  // stack when the POS Cart card is there, the cart columns when Supervised AI
  // mode is on. Validating a hidden row would block Apply on a value the agent
  // cannot see to fix.
  const isValid =
    panesValid &&
    leftValid &&
    (!isPosCartEnabled || rightValid) &&
    (!isShadowModeEnabled || cartValid);

  // Any manual edit means the pending Apply is no longer a plain reset.
  const setPaneValue = (index: number, value: number) => {
    const next = [...paneValues];
    next[index] = value;
    setPaneValues(next);
    setPendingReset(false);
  };
  const setLeftValue = (index: number, value: number) => {
    const next = [...leftValues];
    next[index] = value;
    setLeftValues(next);
    setPendingReset(false);
  };
  const setRightValue = (index: number, value: number) => {
    const next = [...rightValues];
    next[index] = value;
    setRightValues(next);
    setPendingReset(false);
  };
  const selectPanePreset = (sizes: number[]) => {
    setPaneValues(sizes);
    setPendingReset(false);
  };
  const selectLeftPreset = (sizes: number[]) => {
    setLeftValues(sizes);
    setPendingReset(false);
  };
  const selectRightPreset = (sizes: number[]) => {
    setRightValues(sizes);
    setPendingReset(false);
  };
  const setCartValue = (index: number, value: number) => {
    const next = [...cartValues];
    next[index] = value;
    setCartValues(next);
    setPendingReset(false);
  };
  const selectCartPreset = (sizes: number[]) => {
    setCartValues(sizes);
    setPendingReset(false);
  };
  const changeDisplay = (next: DisplayPreference) => {
    setDisplayValues(next);
    setPendingReset(false);
  };

  const handleApply = () => {
    if (!isValid) return;
    // A still-pending reset clears the saved keys; otherwise commit the values.
    if (pendingReset) {
      onReset();
    } else {
      onApply({
        panes: [paneValues[0], paneValues[1], paneValues[2]],
        leftStack: [leftValues[0], leftValues[1]],
        rightStack: [rightValues[0], rightValues[1]],
        cartSplit: [cartValues[0], cartValues[1]],
      });
      if (isHitl7) onApplyDisplay?.(displayValues);
    }
    onClose();
  };

  // Stage the defaults into the working values; nothing is committed until the
  // user clicks Apply (the dialog stays open so they can review).
  const handleReset = () => {
    setPaneValues(DEFAULT_PANE_SIZES.slice());
    setLeftValues(LEFT_STACK_DEFAULT_SIZES.slice());
    setRightValues(RIGHT_STACK_DEFAULT_SIZES.slice());
    setCartValues(CART_SPLIT_DEFAULT_SIZES.slice());
    setDisplayValues(DEFAULT_DISPLAY_PREFERENCE);
    setPendingReset(true);
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      // index.css sets `body` to the system font, and only MUI Typography/Button
      // pull in the theme font — so plain <span>/<button> text would otherwise
      // render in the system font. Force the app font ('Prompt') on the dialog
      // paper so every element inherits it consistently.
      PaperProps={{
        sx: (theme) => ({ fontFamily: theme.typography.fontFamily }),
      }}
      // Marks the dialog root so the AI auto-mode intercept listener in
      // AIStatusBar ignores interactions here (resizing/presets aren't a
      // "take over the order" action).
      data-layout-preference="true"
    >
      <Box className={classes.titleBar}>
        <div className={classes.titleLeft}>
          <div className={classes.titleIcon}>
            <ViewWeekOutlinedIcon fontSize="inherit" />
          </div>
          <Typography sx={sectionHeadingSx}>
            {isHitl7 ? 'Preferences' : 'Layout preference'}
          </Typography>
        </div>
        <IconButton onClick={onClose} size="small" aria-label="Close">
          <CloseIcon />
        </IconButton>
      </Box>

      <Tabs
        value={tab}
        onChange={(_event, value) => setTab(value)}
        sx={{
          px: 3,
          minHeight: 0,
          borderBottom: `1px solid ${Colors.alto}`,
          '& .MuiTab-root': {
            minHeight: 0,
            py: 1,
            textTransform: 'none',
            fontSize: '0.8rem',
            fontWeight: 700,
          },
        }}
      >
        <Tab label="Pane width" />
        <Tab label="Card height" />
        {isHitl7 && <Tab label="Display" />}
      </Tabs>

      {/* sx (not makeStyles) for padding/margins/fonts on every MUI component
          below: @mui/styles (JSS) injects before MUI's emotion styles, so a
          makeStyles class loses to MUI's own defaults. sx wins. */}
      <DialogContent sx={{ px: 3, pt: 2, pb: 0 }}>
        {tab === 0 && (
          <>
            <Typography className={classes.desc} sx={{ fontSize: '0.75rem' }}>
              Adjust the width of each pane as a percentage.
            </Typography>
            <Typography className={classes.desc} sx={{ fontSize: '0.75rem' }}>
              The three values must add up to 100% (each between {MIN_PANE_SIZE}
              % and {MAX_PANE_SIZE}%).
            </Typography>

            <SplitGroup
              items={PANE_ITEMS}
              values={paneValues}
              min={MIN_PANE_SIZE}
              max={MAX_PANE_SIZE}
              unitLabel="width"
              onChange={setPaneValue}
            />

            <TotalBanner
              total={paneTotal}
              isValid={panesValid}
              validHint="All good! Your layout adds up to 100%."
              invalidHint="The three values must add up to 100%."
            />

            <Typography
              sx={(theme) => ({ ...sectionHeadingSx(theme), mt: 3, mb: 1.5 })}
            >
              Quick presets
            </Typography>
            <PresetRow
              presets={PANE_PRESETS}
              values={paneValues}
              orientation="horizontal"
              onSelect={selectPanePreset}
            />

            {/*
              HITL-1648 — the cart columns live on the WIDTH tab, beneath the
              panes, because that is what they are: a width split nested inside
              the right pane. Putting them on "Card height" would file them by
              where they sit rather than by what the agent is adjusting.
            */}
            {isShadowModeEnabled && (
              <>
                <Typography
                  sx={(theme) => ({ ...sectionHeadingSx(theme), mt: 3 })}
                  data-testid="cart-split-heading"
                >
                  Right pane columns
                </Typography>
                <Typography
                  className={classes.desc}
                  sx={{ fontSize: '0.75rem' }}
                >
                  Split the cart card between your own cart and the live order
                  (each between {CART_SPLIT_MIN}% and {CART_SPLIT_MAX}%).
                </Typography>
                <SplitGroup
                  items={CART_SPLIT_ITEMS}
                  values={cartValues}
                  min={CART_SPLIT_MIN}
                  max={CART_SPLIT_MAX}
                  unitLabel="width"
                  onChange={setCartValue}
                />
                <TotalBanner
                  total={cartTotal}
                  isValid={cartValid}
                  validHint="All good! These columns add up to 100%."
                  invalidHint="The two values must add up to 100%."
                />
                <Box sx={{ mt: 1.5 }}>
                  <PresetRow
                    presets={CART_SPLIT_PRESETS}
                    values={cartValues}
                    // Horizontal, like the panes above and unlike the two card
                    // stacks below: this is the one nested split that divides
                    // by WIDTH, so its glyph has to show bars side by side. A
                    // vertical glyph would draw the preview as stacked rows,
                    // which is the picture of the thing it is not.
                    orientation="horizontal"
                    onSelect={selectCartPreset}
                  />
                </Box>
              </>
            )}
          </>
        )}

        {tab === 1 && (
          <>
            <Typography className={classes.desc} sx={{ fontSize: '0.75rem' }}>
              Adjust the height of the cards in each pane as a percentage.
            </Typography>
            <Typography className={classes.desc} sx={{ fontSize: '0.75rem' }}>
              Each pane's cards must add up to 100% (each between{' '}
              {CARD_MIN_HEIGHT}% and {CARD_MAX_HEIGHT}%).
            </Typography>

            <Typography sx={(theme) => ({ ...sectionHeadingSx(theme), mt: 3 })}>
              Left pane
            </Typography>
            <SplitGroup
              items={LEFT_STACK_ITEMS}
              values={leftValues}
              min={CARD_MIN_HEIGHT}
              max={CARD_MAX_HEIGHT}
              unitLabel="height"
              onChange={setLeftValue}
            />
            <TotalBanner
              total={leftTotal}
              isValid={leftValid}
              validHint="All good! These cards add up to 100%."
              invalidHint="The two values must add up to 100%."
            />
            <Box sx={{ mt: 1.5 }}>
              <PresetRow
                presets={LEFT_STACK_PRESETS}
                values={leftValues}
                orientation="vertical"
                onSelect={selectLeftPreset}
              />
            </Box>

            <Typography sx={(theme) => ({ ...sectionHeadingSx(theme), mt: 3 })}>
              Right pane
            </Typography>
            {isPosCartEnabled ? (
              <>
                <SplitGroup
                  items={RIGHT_STACK_ITEMS}
                  values={rightValues}
                  min={CARD_MIN_HEIGHT}
                  max={CARD_MAX_HEIGHT}
                  unitLabel="height"
                  onChange={setRightValue}
                />
                <TotalBanner
                  total={rightTotal}
                  isValid={rightValid}
                  validHint="All good! These cards add up to 100%."
                  invalidHint="The two values must add up to 100%."
                />
                <Box sx={{ mt: 1.5 }}>
                  <PresetRow
                    presets={RIGHT_STACK_PRESETS}
                    values={rightValues}
                    orientation="vertical"
                    onSelect={selectRightPreset}
                  />
                </Box>
              </>
            ) : (
              <div className={classes.posOffNote}>
                POS Cart is off — Agent Cart fills the pane. Turn on the POS
                Cart to adjust its height.
              </div>
            )}
          </>
        )}
        {isHitl7 && tab === 2 && (
          <DisplayPreferenceTab
            value={displayValues}
            onChange={changeDisplay}
          />
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pt: 3, pb: 2 }}>
        <Button
          // Matches the green "Total: 100%" label exactly (shared statusLabelSx).
          sx={(theme) => ({ ...statusLabelSx(theme), color: Colors.welcome })}
          onClick={handleReset}
          startIcon={<CachedIcon />}
        >
          Reset all to default
        </Button>
        <Box sx={{ flex: 1 }} />
        <Button variant="outlined" onClick={onClose}>
          Cancel
        </Button>
        <Button variant="contained" onClick={handleApply} disabled={!isValid}>
          Apply
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default LayoutPreferenceDialog;
